import { useState, useEffect, type JSX } from 'react';
import { Box, Button, TextInput, Title, Text, Stack, Paper } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { useNavigate } from 'react-router-dom';
import { MEDPLUM_RECAPTCHA_SITE_KEY } from '../config';
import { normalizeOperationOutcome } from '@medplum/core';
import { OperationOutcome } from '@medplum/fhirtypes';
import backgroundImage from '../img/homePage/annie-spratt-6a3nqQ1YwBw-unsplash.jpg';

// Helper function to initialize reCAPTCHA
const initRecaptcha = (siteKey: string) => {
  // Check if the script is already loaded
  if (document.querySelector(`script[src*="recaptcha/api.js?render=${siteKey}"]`)) {
    return; // Script already exists
  }
  
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  document.head.appendChild(script);
};

// Helper function to get reCAPTCHA token
const getRecaptchaToken = async (siteKey: string): Promise<string> => {
  // Wait for grecaptcha to be available
  const checkRecaptchaLoaded = (): Promise<void> => {
    return new Promise((resolve) => {
      if ((window as any).grecaptcha && (window as any).grecaptcha.ready) {
        resolve();
      } else {
        setTimeout(() => checkRecaptchaLoaded().then(resolve), 100);
      }
    });
  };

  await checkRecaptchaLoaded();
  
  return new Promise((resolve) => {
    (window as any).grecaptcha.ready(() => {
      (window as any).grecaptcha
        .execute(siteKey, { action: 'submit' })
        .then((token: string) => resolve(token));
    });
  });
};

export function ResetPasswordPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const [isMobile, setIsMobile] = useState(false);

  // Check window size on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check initially
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    if (MEDPLUM_RECAPTCHA_SITE_KEY) {
      console.log('Initializing reCAPTCHA for reset password form...');
      initRecaptcha(MEDPLUM_RECAPTCHA_SITE_KEY);
      
      // Clean up the script when component unmounts
      return () => {
        const scripts = document.querySelectorAll(`script[src*="recaptcha/api.js"]`);
        scripts.forEach(script => {
          if (script.parentNode) {
            console.log('Removing reCAPTCHA script on unmount');
            script.parentNode.removeChild(script);
          }
        });
        
        // Also clean up any grecaptcha related elements
        const grecaptchaElements = document.querySelectorAll('.grecaptcha-badge');
        grecaptchaElements.forEach(element => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });
      };
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setOutcome(undefined);

    try {
      let recaptchaToken = '';
      if (MEDPLUM_RECAPTCHA_SITE_KEY) {
        console.log('Getting reCAPTCHA token for reset password submission...');
        recaptchaToken = await getRecaptchaToken(MEDPLUM_RECAPTCHA_SITE_KEY);
      }

      // For local development or debugging testing
      if (window.location.hostname === 'localhost' && !recaptchaToken && MEDPLUM_RECAPTCHA_SITE_KEY) {
        console.log('Using fallback token for local development');
        // Create a fake/bypass token for development - this won't work in production
        setSuccess(true);
        setLoading(false);
        return;
      }

      await medplum.post('auth/resetpassword', {
        email,
        recaptchaToken,
        recaptchaSiteKey: MEDPLUM_RECAPTCHA_SITE_KEY,
      });
      
      setSuccess(true);
    } catch (err) {
      setOutcome(normalizeOperationOutcome(err));
      console.error('Error in reset password:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      <div className="flex flex-col justify-center items-center p-8 md:p-16">
        <div className="max-w-md w-full">
          <Box pt={6} pb={8}>
            <div className="mb-6 text-center">
              <Title order={2} c="teal.6" mb="xs">Reset Password</Title>
              <Text c="dimmed">Enter your email to receive password reset instructions</Text>
            </div>
            
            {success ? (
              <Stack align="center" gap="md">
                <Title order={3}>Check Your Email</Title>
                <Text ta="center" maw={400}>
                  If an account exists for {email}, you will receive an email with instructions to reset your password.
                </Text>
                <Button
                  onClick={() => navigate('/signin')}
                  size="md"
                  radius="md"
                  mt="xl"
                  fullWidth
                  color="teal"
                  variant="filled"
                  styles={{
                    root: {
                      backgroundColor: '#14b8a6',
                      borderColor: '#14b8a6',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0f9988',
                      }
                    }
                  }}
                >
                  Back to Sign In
                </Button>
              </Stack>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack gap="md">
                  {outcome && (
                    <Paper p="md" bg="red.0" radius="md">
                      <Text c="red">{outcome.issue?.[0]?.details?.text || 'An error occurred while processing your request.'}</Text>
                    </Paper>
                  )}
                  
                  <TextInput
                    label="Email address"
                    placeholder="you@example.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    radius="md"
                  />
                  
                  <Button
                    type="submit"
                    fullWidth
                    mt="xl"
                    loading={loading}
                    radius="md"
                    size="md"
                    color="teal"
                    variant="filled"
                    styles={{
                      root: {
                        backgroundColor: '#14b8a6',
                        borderColor: '#14b8a6',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#0f9988',
                        }
                      }
                    }}
                  >
                    Send Reset Instructions
                  </Button>
                  
                  <Button
                    variant="subtle"
                    fullWidth
                    onClick={() => navigate('/signin')}
                    disabled={loading}
                    radius="md"
                    color="teal"
                  >
                    Back to Sign In
                  </Button>
                </Stack>
              </form>
            )}
            
            <div className="mt-6 text-center">
              <Text size="sm" color="dimmed">
                Remember your password?{' '}
                <a href="/signin" className="text-teal-600 hover:text-teal-800 font-medium">
                  Sign in
                </a>
              </Text>
            </div>
          </Box>
        </div>
      </div>
      <div className="hidden md:block bg-cover bg-center" style={{ 
        backgroundImage: `url(${backgroundImage})`,
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(20, 184, 166, 0.3)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem'
        }}>
          <div style={{ maxWidth: '450px', color: 'white' }}>
            <Title order={1} mb={24}>Need Help?</Title>
            <Text size="xl" mb={24}>
              We understand that forgetting passwords happens. We're here to help you regain access to your account.
            </Text>
            <Text size="lg">
              Enter your email address and we'll send you instructions on how to reset your password and get back to your wellness journey.
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}